import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
} from 'ag-grid-community'
import { styled } from '@mui/material'
import _ from 'lodash'
import { useIntl } from 'react-intl'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import Loading from '../../components/process-state-notifications/Loading'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { usePageState } from '../../hooks/usePageState'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useWorkloadUnit } from '../../hooks/useWorkloadUnit'
import store from '../../../store'
import { requireSave } from '../../../store/requiredSaveData'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { AggregateField } from '../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../lib/functions/workload'
import { useProgramProjectReportGridOptions } from './gridOptions'
import { statusFilterOptions } from './gridOptions/columnDefs'
import { useProgramProjectReportData } from './hooks/useProgramProjectReportData'
import { useUnitSetting } from './hooks/useUnitSetting'
import {
  PROGRAM_PROJECT_REPORT_ROW_TYPE,
  ProgramProjectReportRow,
} from './model/reportRow'
import { UnitHeader } from './components/UnitHeader'
import { UnitSettingDialog } from './components/UnitSettingDialog'

const ProgramProjectReportPage = ({ uuid }: PageProps) => {
  const intl = useIntl()
  const gridRef = useRef<AgGridReact<ProgramProjectReportRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const [gridApi, setGridApi] = useState<GridApi>()
  const [saving, setSaving] = useState(false)
  const { data, loading, fetch, save, savedAggregateField } =
    useProgramProjectReportData()

  const { workloadUnit, updatePageState } = usePageState(uuid, {
    workloadUnit: WorkloadUnit.HOUR,
  })
  const setWorkloadUnit = useCallback(
    (unit: WorkloadUnit) => updatePageState({ workloadUnit: unit }),
    [updatePageState]
  )

  const unitSetting = useUnitSetting(
    savedAggregateField,
    workloadUnit,
    setWorkloadUnit
  )
  const gridOptions = useProgramProjectReportGridOptions()
  const workloadUnitState = useWorkloadUnit(workloadUnit)
  const gridContext = useMemo(
    () => ({
      programProjectReportTypes: Object.values(PROGRAM_PROJECT_REPORT_ROW_TYPE),
      programProjectReportStatuses: statusFilterOptions,
      workloadUnitState,
      aggregateField: unitSetting.aggregateField,
    }),
    [workloadUnitState, unitSetting.aggregateField]
  )
  useEffect(() => {
    gridApi?.refreshCells({ force: true })
    gridApi?.refreshHeader()
  }, [gridApi, gridContext])
  const [isChangeDialogOpen, setChangeDialogOpen] = useState(false)
  const isInitialDialogOpen = !loading && !unitSetting.isUnitSettled

  const bulkSheetState = useBulkSheetState(
    uuid,
    UiStateKey.ProgramProjectReportColumnAndFilterState
  )
  const columnState = useColumnState({
    gridApi,
    autoGroupColumnDef: gridOptions.autoGroupColumnDef,
    bulkSheetState,
  })
  const columnSetting = useColumnSetting()

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api)
      if (!_.isEmpty(bulkSheetState.columnState)) {
        event.api.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
        return
      }
      const columnState = event.api.getColumnState()
      const nameIndex = columnState.findIndex(
        state => state.colId === 'ag-Grid-AutoColumn'
      )
      const statusIndex = columnState.findIndex(
        state => state.colId === 'status'
      )
      if (nameIndex < 0 || statusIndex < 0) return
      const [nameState] = columnState.splice(nameIndex, 1)
      const insertIndex =
        columnState.findIndex(state => state.colId === 'status') + 1
      columnState.splice(insertIndex, 0, nameState)
      event.api.applyColumnState({ state: columnState, applyOrder: true })
    },
    [bulkSheetState.columnState]
  )

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      if (!_.isEmpty(bulkSheetState.filterState)) {
        event.api.setFilterModel(bulkSheetState.filterState ?? null)
      }
      bulkSheetState.finishRestoring()
    },
    [bulkSheetState.filterState, bulkSheetState.finishRestoring]
  )

  const onCellValueChanged = useCallback(() => {
    store.dispatch(requireSave())
  }, [])

  const persist = useCallback(
    async (
      aggregateField: AggregateField,
      options?: { clearProgress?: boolean }
    ): Promise<boolean> => {
      setSaving(true)
      try {
        const response = await save(aggregateField, options)
        if (!response.hasError && !response.hasWarning) {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.SUCCESS,
              title: intl.formatMessage({ id: 'registration.complete' }),
            })
          )
          await fetch()
          return true
        }
        return false
      } finally {
        setSaving(false)
      }
    },
    [save, fetch, intl]
  )

  const onSubmit = useCallback(async () => {
    gridApi?.stopEditing()
    await persist(unitSetting.aggregateField)
  }, [gridApi, unitSetting.aggregateField, persist])

  const onSettleUnit = useCallback(
    async (field: AggregateField, unit: WorkloadUnit) => {
      unitSetting.selectUnit(field, unit)
      await persist(field)
    },
    [unitSetting, persist]
  )

  const onChangeUnit = useCallback(
    async (
      field: AggregateField,
      unit: WorkloadUnit,
      deleteExistingData: boolean
    ) => {
      unitSetting.selectUnit(field, unit)
      setChangeDialogOpen(false)
      await persist(field, { clearProgress: deleteExistingData })
    },
    [unitSetting, persist]
  )

  useKeyBind(
    [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
    [onSubmit]
  )

  return (
    <PageArea>
      <SavePopper loading={saving} onSubmit={onSubmit} />
      <Header>
        <ReloadButton onReload={fetch} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
        {unitSetting.isUnitSettled && (
          <UnitHeader
            aggregateField={unitSetting.aggregateField}
            workloadUnit={unitSetting.workloadUnit}
            onChangeWorkloadUnit={unitSetting.setWorkloadUnit}
            onClickChange={() => setChangeDialogOpen(true)}
          />
        )}
      </Header>
      {bulkSheetState.initialized && (
        <BulkSheetView
          ref={bulkSheetRef}
          gridRef={gridRef}
          gridOptions={gridOptions}
          context={gridContext}
          rowData={data}
          onGridReady={handleGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onCellValueChanged={onCellValueChanged}
          onColumnVisible={columnState.onColumnVisible}
          onColumnResized={columnState.rememberColumnState}
          onColumnMoved={columnState.rememberColumnState}
          onFilterChanged={columnState.onFilterChanged}
          onSortChanged={columnState.onSortChanged}
        />
      )}
      <Loading
        isLoading={!bulkSheetState.initialized || loading}
        elem={bulkSheetRef.current}
      />

      <UnitSettingDialog
        mode="initial"
        isOpen={isInitialDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={WorkloadUnit.HOUR}
        onSubmit={onSettleUnit}
      />
      <UnitSettingDialog
        mode="change"
        isOpen={isChangeDialogOpen}
        defaultField={unitSetting.aggregateField}
        defaultWorkloadUnit={unitSetting.workloadUnit}
        onSubmit={onChangeUnit}
        onClose={() => setChangeDialogOpen(false)}
      />
      {columnSetting.isOpen && (
        <ColumnSettingPopper
          anchorEl={columnSetting.anchorEl}
          open={columnSetting.isOpen}
          close={columnSetting.close}
          gridApi={gridApi}
          height={bulkSheetRef.current?.offsetHeight}
          openSavedUiStateDialog={undefined}
          initializeColumnState={columnState.resetColumnState}
          applicationFunctionUuid={uuid}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          columnState={bulkSheetState.columnState}
          offset={120}
        />
      )}
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export const ProgramProjectReport = projectPrivate(ProgramProjectReportPage)
